package com.example.mystybag.appCore.views.pages

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.DeliveryPageViewModel
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.custom.ShimmerAsyncImage
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonSize
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.expected.DateTimeHelper
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p18
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p64
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.sharedmodels.project.database.UserReservationModel
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.roundToInt
import kotlin.math.sin

/** True if reservation belongs in history tab. */
private fun isHistoryReservation(reservation: UserReservationModel): Boolean {
    val status = ReservationStatus.fromInt(reservation.status)
    // Terminal / completed flows only. PAID and RESERVED stay on Active so the user can still
    // swipe to confirm collection; History cards hide that control (isHistoryCard -> false).
    // Pickup window expiry is shown via isPickupExpired on Active, not by moving rows to History.

    return when (status) {
        ReservationStatus.CANCELED,
        ReservationStatus.DELIVERED_TO_CLIENT,
        ReservationStatus.PAID_TO_MERCHANT -> true
        else -> {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            if (nowMs > reservation.dateTimePickUpTo)
                return true
            else
                false
        }
    }
}

/** True if status is PAID and the pickup window has passed (show as "Неприета"). */
private fun isPickupExpired(reservation: UserReservationModel): Boolean {
    if (reservation.dateTimePickUpTo <= 0L) return false
    return Clock.System.now().toEpochMilliseconds() > reservation.dateTimePickUpTo
}

/**
 * Formats the pickup window as "dd.MM.yyyy HH:mm – HH:mm" when both ends fall on the same day,
 * or "dd.MM.yyyy HH:mm – dd.MM.yyyy HH:mm" otherwise. Returns null when no valid window is available.
 */
private fun formatPickupWindow(dateTimeFrom: Long, dateTimeTo: Long): String? {
    if (dateTimeFrom <= 0L || dateTimeTo <= 0L) return null
    val tz = TimeZone.currentSystemDefault()
    val fromLdt = Instant.fromEpochMilliseconds(dateTimeFrom).toLocalDateTime(tz)
    val toLdt = Instant.fromEpochMilliseconds(dateTimeTo).toLocalDateTime(tz)
    val fromDate = formatLocalDate(fromLdt)
    val toDate = formatLocalDate(toLdt)
    val fromTime = formatLocalTime(fromLdt)
    val toTime = formatLocalTime(toLdt)
    return if (fromDate == toDate) {
        "$fromDate $fromTime – $toTime"
    } else {
        "$fromDate $fromTime – $toDate $toTime"
    }
}

private fun formatLocalDate(ldt: kotlinx.datetime.LocalDateTime): String {
    val day = ldt.dayOfMonth.toString().padStart(2, '0')
    val month = ldt.monthNumber.toString().padStart(2, '0')
    return "$day.$month.${ldt.year}"
}

private fun formatLocalTime(ldt: kotlinx.datetime.LocalDateTime): String {
    val hour = ldt.hour.toString().padStart(2, '0')
    val minute = ldt.minute.toString().padStart(2, '0')
    return "$hour:$minute"
}

private fun sortedHistoryReservations(reservations: List<UserReservationModel>): List<UserReservationModel> =
    reservations
        .filter { isHistoryReservation(it) }
        .sortedWith(
            compareByDescending<UserReservationModel> {
                it.dateTimeReceived ?: it.dateTimeReserved
            }.thenByDescending { it.id }
        )

@Composable
fun DeliveryPageView(
    viewModel: DeliveryPageViewModel,
    webColumns: Int = 1
) {
    val uiState by viewModel.uiState.subscribeAsState()
    val isWeb = PlatformHelper.isWeb
    val maxContentWidth = if (isWeb) 1400.dp else Dp.Infinity
    val webLayoutWidth = 1080.dp
    val webGridSpacing = 20.dp
    val activeReservations = uiState.reservations
        .filter { !isHistoryReservation(it) }
    val historyReservations = sortedHistoryReservations(uiState.reservations)
    val currentList = if (uiState.selectedTabIndex == 0) activeReservations else historyReservations
    val listState = rememberLazyListState()

    LaunchedEffect(uiState.scrollHistoryToTopPending, uiState.selectedTabIndex, uiState.reservations) {
        if (!uiState.scrollHistoryToTopPending || uiState.selectedTabIndex != 1) return@LaunchedEffect
        if (sortedHistoryReservations(uiState.reservations).isEmpty()) return@LaunchedEffect
        listState.scrollToItem(0)
        viewModel.consumeScrollHistoryToTopPending()
    }

    BottomSheetUIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        removeVerticalPadding = true,
        applyBottomInsetSpacer = false,
        removeBottomSheetPadding = true,
        navigationContent = null,
        showTopBar = false,
        bottomSheetFactory = {}
    ) { state ->
        when {
            state.value.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = AppTheme.current.colors.primary)
                }
            }
            state.value.errorMessage != null -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(p16),
                    contentAlignment = Alignment.Center
                ) {
                    Texts.TitleMedium(
                        text = state.value.errorMessage ?: "",
                        color = AppTheme.current.colors.error,
                        textAlign = TextAlign.Center
                    )
                }
            }
            else -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.TopCenter
                ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .widthIn(max = maxContentWidth)
                ) {
                    // Figma: Frame 103 – 380x44; Display Small SemiBold 36, On Surface; 80dp from status bar to title
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = p32, bottom = 20.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .then(if (isWeb && webColumns > 1) Modifier.width(webLayoutWidth) else Modifier)
                                .fillMaxWidth()
                                .height(44.dp)
                                .padding(horizontal = p16),
                            contentAlignment = Alignment.CenterStart
                        ) {
                            Texts.DisplaySmall(
                                text = sharedString(SR.strings.my_reservations),
                                color = AppTheme.current.colors.onSurface,
                                textAlign = TextAlign.Start
                            )
                        }
                    }
                    // Segment Control — Figma: 380×48, cornerRadius 200, padding 4; sliding pill animation
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .then(if (isWeb && webColumns > 1) Modifier.width(webLayoutWidth) else Modifier)
                                .fillMaxWidth()
                                .height(48.dp)
                                .padding(horizontal = if (isWeb) p24 else p16)
                                .clip(RoundedCornerShape(200.dp))
                                .background(AppTheme.current.colors.surfaceContainerLowest)
                                .padding(4.dp)
                        ) {
                            BoxWithConstraints(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(40.dp)
                            ) {
                                val segmentWidth = maxWidth / 2
                                val fraction by animateFloatAsState(
                                    targetValue = state.value.selectedTabIndex.toFloat(),
                                    animationSpec = tween(durationMillis = 250),
                                    label = "segmentIndicator"
                                )
                                val slidingOffsetDp = (segmentWidth.value * fraction).dp
                                // Sliding white pill (behind text)
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .offset(x = slidingOffsetDp)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth(0.5f)
                                            .fillMaxHeight()
                                            .clip(RoundedCornerShape(200.dp))
                                            .background(AppTheme.current.colors.surface)
                                    )
                                }
                                // Labels on top (no background, clickable)
                                Row(
                                    modifier = Modifier.fillMaxSize(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    listOf(
                                        sharedString(SR.strings.tab_active),
                                        sharedString(SR.strings.tab_history)
                                    ).forEachIndexed { index, label ->
                                        val selected = state.value.selectedTabIndex == index
                                        Box(
                                            modifier = Modifier
                                                .weight(1f)
                                                .fillMaxWidth()
                                                .height(40.dp)
                                                .clickable(
                                                    interactionSource = remember { MutableInteractionSource() },
                                                    indication = null
                                                ) {
                                                    viewModel.onTabSelected(index)
                                                },
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Texts.TitleMedium(
                                                modifier = Modifier.padding(horizontal = p40, vertical = p8),
                                                text = label,
                                                color = if (selected) AppTheme.current.colors.primary
                                                else AppTheme.current.colors.onSurfaceVariant
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                    // List or empty state — Figma: Frame 26080511 (340×256), itemSpacing 16
                    if (currentList.isEmpty()) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(horizontal = p16)
                                .padding(top = p48),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(p16)
                        ) {
                            // Frame 135159: bag image 200×152 (Mask group 114×152)
                            Box(
                                modifier = Modifier
                                    .height(152.dp)
                                    .width(200.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    painter = painterResource(SRC.images.add_first_bag_icon),
                                    contentDescription = null,
                                    modifier = Modifier
                                        .height(152.dp)
                                        .width(114.dp),
                                    contentScale = ContentScale.Fit
                                )
                            }
                            // Don't have any active reservations yet. — 340×24, Title Medium 16
                            Texts.TitleMedium(
                                text = sharedString(SR.strings.no_active_reservations_yet),
                                textAlign = TextAlign.Center,
                                color = AppTheme.current.colors.onSurface,
                                modifier = Modifier.fillMaxWidth()
                            )
                            // Button: 105×48, cornerRadius 100, padding 20×12 — Primary Container bg, Primary text
                            DefaultButton(
                                modifier = Modifier.width(200.dp).height(48.dp),
                                caption = sharedString(SR.strings.find_bag),
                                size = DefaultButtonSize.MEDIUM,
                                type = DefaultButtonType.SECONDARY,
                                callback = { viewModel.onFindBagClick() }
                            )
                        }
                    } else {
                        val useTwoColumnWebGrid = isWeb && webColumns > 1
                        val horizontalPadding = if (isWeb) p24 else p16
                        if (useTwoColumnWebGrid) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(top = p24),
                                contentAlignment = Alignment.TopCenter
                            ) {
                                LazyVerticalGrid(
                                    columns = GridCells.Fixed(webColumns),
                                    modifier = Modifier
                                        .width(webLayoutWidth)
                                        .fillMaxHeight()
                                        .scrollEdgeFade(showTop = false),
                                    horizontalArrangement = Arrangement.spacedBy(webGridSpacing),
                                    verticalArrangement = Arrangement.spacedBy(p16)
                                ) {
                                    items(
                                        items = currentList,
                                        key = { it.id }
                                    ) { reservation ->
                                        ReservationCard(
                                            reservation = reservation,
                                            extra = uiState.reservationExtras[reservation.id],
                                            isHistoryCard = uiState.selectedTabIndex == 1,
                                            onPickupAddressClick = { viewModel.onPickupAddressClick(reservation.id) },
                                            onDetailsClick = { viewModel.onReservationDetailsClick(reservation) },
                                            onLeaveReviewClick = { viewModel.onReservationClick(reservation) },
                                            onUnlockForDeliveryClick = { viewModel.onUnlockForDeliveryClick(reservation) }
                                        )
                                    }
                                }
                            }
                        } else {
                            LazyColumn(
                                state = listState,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(
                                        start = horizontalPadding,
                                        end = horizontalPadding,
                                        top = p24
                                    )
                                    .scrollEdgeFade(showTop = false),
                                verticalArrangement = Arrangement.spacedBy(p12)
                            ) {
                                items(
                                    items = currentList,
                                    key = { it.id }
                                ) { reservation ->
                                    ReservationCard(
                                        reservation = reservation,
                                        extra = uiState.reservationExtras[reservation.id],
                                        isHistoryCard = uiState.selectedTabIndex == 1,
                                        onPickupAddressClick = { viewModel.onPickupAddressClick(reservation.id) },
                                        onDetailsClick = { viewModel.onReservationDetailsClick(reservation) },
                                        onLeaveReviewClick = { viewModel.onReservationClick(reservation) },
                                        onUnlockForDeliveryClick = { viewModel.onUnlockForDeliveryClick(reservation) }
                                    )
                                }
                            }
                        }
                    }
                }
                }
            }
        }
    }
}

// Figma: Reservation Card — 380×260, cornerRadius 28, padding 16, itemSpacing 10
@Composable
private fun ReservationCard(
    reservation: com.example.mystybag.sharedmodels.project.database.UserReservationModel,
    extra: com.example.mystybag.appCore.model.pages.ReservationCardExtra?,
    isHistoryCard: Boolean,
    onPickupAddressClick: () -> Unit,
    onDetailsClick: () -> Unit,
    onLeaveReviewClick: () -> Unit,
    onUnlockForDeliveryClick: () -> Unit
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, AppTheme.current.colors.outline, RoundedCornerShape(p28))
            .clickable(onClick = onDetailsClick),
        shape = RoundedCornerShape(p28),
        colors = CardDefaults.cardColors(
            containerColor = AppTheme.current.colors.surface
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p16),
            verticalArrangement = Arrangement.spacedBy(p16)
        ) {
            // Frame 26080545: content 348×228, itemSpacing 16
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p16)
            ) {
                // Frame 26080543: top row 348×64 — image + title/subtitle + status chip
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(p16),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        modifier = Modifier.weight(1f),
                        horizontalArrangement = Arrangement.spacedBy(p16),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier
                                .size(p64)
                                .clip(RoundedCornerShape(16.dp))
                        ) {
                            val imageUrl = reservation.merchantCoverPhotoUrl.takeIf { it.isNotBlank() }
                            if (imageUrl != null) {
                                ShimmerAsyncImage(
                                    modifier = Modifier.fillMaxSize(),
                                    link = imageUrl,
                                    contentScale = ContentScale.Crop
                                )
                            } else {
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .background(AppTheme.current.colors.surfaceContainerHighest)
                                )
                            }
                        }
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(p4)
                        ) {
                            val bagTitle =
                                reservation.bagName?.takeIf { it.isNotBlank() }
                                    ?: extra?.bagTitleFromDetails?.takeIf { it.isNotBlank() }
                                    ?: sharedString(SR.strings.surprise_bag)
                            Texts.TitleSmall(
                                text = bagTitle,
                                color = AppTheme.current.colors.onSurface,
                                maxLines = 2
                            )
                            Texts.BodySmall(
                                text = "${reservation.bagsCount} ${sharedString(SR.strings.bags_count)}",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                        }
                    }
                    ReservationStatusBadge(
                        status = ReservationStatus.fromInt(reservation.status),
                        isHistoryCard = isHistoryCard,
                        isPickupExpired = isPickupExpired(reservation)
                    )
                }

                if (isHistoryCard) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        val locationRating = extra?.locationAverageRating?.coerceIn(0.0, 5.0) ?: 0.0
                        Row(horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                            repeat(5) { index ->
                                val fillFraction = (locationRating - index).coerceIn(0.0, 1.0)
                                FractionalStar(
                                    modifier = Modifier.size(16.dp, 16.dp),
                                    fillFraction = fillFraction.toFloat(),
                                    filledColor = AppTheme.current.colors.tertiary,
                                    unfilledColor = AppTheme.current.colors.outlineVariant
                                )
                            }
                        }

                        val acceptedAt  = reservation.dateTimeReceived ?: reservation.dateTimeReserved
                        val dateValue   = DateTimeHelper.parseDateFromMilliseconds(acceptedAt)

                        Texts.BodyMedium(
                            text = "Прието на: $dateValue",
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                        Texts.BodyMedium(
                            text = "${sharedString(SR.strings.reservation_number)}: ${reservation.id}",
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                    }
                } else {
                    // Адрес за получаване + Време за получаване (вместо Вземи, ID, Резервирано на)
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(p8)
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Texts.BodySmall(
                                text = sharedString(SR.strings.reservation_number) + ":",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                            Texts.BodySmall(
                                text = "${reservation.id}",
                                modifier = Modifier.weight(1f),
                                color = AppTheme.current.colors.onSurface,
                                textAlign = TextAlign.End
                            )
                        }
                        val pickupAddress = extra?.pickupAddress?.takeIf { it.isNotBlank() }
                        val hasAddress = pickupAddress != null
                        Row(
                            modifier = Modifier
                                .fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Texts.BodySmall(
                                text = sharedString(SR.strings.pickup_address) + ":",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                            AutoResizeSingleLineBodySmall(
                                text = pickupAddress ?: "—",
                                modifier = Modifier
                                    .weight(1f)
                                    .then(
                                        if (hasAddress) Modifier.clickable(onClick = onPickupAddressClick)
                                        else Modifier
                                    ),
                                color = if (hasAddress) AppTheme.current.colors.onPrimaryContainer else AppTheme.current.colors.onSurfaceVariant,
                                textDecoration = if (hasAddress) TextDecoration.Underline else null,
                                textAlign = TextAlign.End
                            )
                        }
                        val collectLabel = formatPickupWindow(
                            dateTimeFrom = reservation.dateTimePickUpFrom,
                            dateTimeTo = reservation.dateTimePickUpTo
                        ) ?: extra?.collectWindowLabel?.takeIf { it.isNotBlank() }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Texts.BodySmall(
                                text = sharedString(SR.strings.collection_time_window) + ":",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                            Texts.BodySmall(
                                text = collectLabel ?: "—",
                                color = AppTheme.current.colors.onSurface
                            )
                        }
                    }
                }

                // Button: "Отключи за доставка" when Reserved/Paid; "Оставете ревю" when !isReviewGiven; else "Детайли на резервация"
                val showUnlockForDelivery = when {
                    isHistoryCard -> false
                    isPickupExpired(reservation) -> false
                    ReservationStatus.fromInt(reservation.status) in listOf(ReservationStatus.RESERVED, ReservationStatus.PAID) -> true
                    else -> false
                }

                val showLeaveReview =
                    !reservation.isReviewGiven &&
                        (reservation.status == ReservationStatus.DELIVERED_TO_CLIENT.code ||
                            reservation.status == ReservationStatus.PAID_TO_MERCHANT.code)

                when {
                    showUnlockForDelivery -> {
                        SlideToConfirmButton(
                            modifier = Modifier.fillMaxWidth(),
                            text = sharedString(SR.strings.swipe_to_confirm_collection),
                            onSlideComplete = onUnlockForDeliveryClick
                        )
                    }
                    showLeaveReview -> {
                        DefaultButton(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(p56),
                            caption = sharedString(SR.strings.leave_review),
                            size = DefaultButtonSize.LARGE,
                            type = DefaultButtonType.SECONDARY_NEUTRAL,
                            callback = onLeaveReviewClick
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AutoResizeSingleLineBodySmall(
    text: String,
    modifier: Modifier = Modifier,
    color: Color,
    textAlign: TextAlign = TextAlign.End,
    textDecoration: TextDecoration? = null
) {
    val baseStyle = AppTheme.current.typography.bodySmall
    val minFontSize = 10.sp
    var currentFontSize by remember(text) { mutableStateOf(baseStyle.fontSize) }

    Text(
        text = text,
        modifier = modifier,
        maxLines = 1,
        softWrap = false,
        overflow = TextOverflow.Ellipsis,
        textAlign = textAlign,
        textDecoration = textDecoration,
        style = baseStyle.copy(color = color, fontSize = currentFontSize),
        onTextLayout = { layoutResult ->
            if (layoutResult.hasVisualOverflow && currentFontSize > minFontSize) {
                currentFontSize = (currentFontSize.value - 0.5f).sp
            }
        }
    )
}

@Composable
private fun AutoResizeSingleLineTitleMedium(
    text: String,
    modifier: Modifier = Modifier,
    color: Color,
    textAlign: TextAlign = TextAlign.Center
) {
    val baseStyle = AppTheme.current.typography.titleMedium
    val minFontSize = 10.sp
    var currentFontSize by remember(text) { mutableStateOf(baseStyle.fontSize) }

    Text(
        text = text,
        modifier = modifier,
        maxLines = 1,
        softWrap = false,
        overflow = TextOverflow.Ellipsis,
        textAlign = textAlign,
        style = baseStyle.copy(color = color, fontSize = currentFontSize),
        onTextLayout = { layoutResult ->
            if (layoutResult.hasVisualOverflow && currentFontSize > minFontSize) {
                currentFontSize = (currentFontSize.value - 0.5f).sp
            }
        }
    )
}

enum class SwipeButtonState {
    IDLE,
    DRAGGING,
    COMPLETED
}

@Composable
private fun FractionalStar(
    modifier: Modifier,
    fillFraction: Float,
    filledColor: Color,
    unfilledColor: Color
) {
    Canvas(modifier = modifier) {
        val clampedFraction = fillFraction.coerceIn(0f, 1f)
        val clipFraction = areaAdjustedClipFraction(clampedFraction)
        val starPath = createStarPath(size.width, size.height)
        drawPath(path = starPath, color = unfilledColor)
        clipRect(right = size.width * clipFraction) {
            drawPath(path = starPath, color = filledColor)
        }
    }
}

private fun areaAdjustedClipFraction(fillFraction: Float): Float {
    val targetAreaFraction = fillFraction.coerceIn(0f, 1f)
    val areaStops = floatArrayOf(
        0f, 0.047f, 0.093f, 0.166f, 0.242f, 0.316f, 0.406f, 0.5f,
        0.6f, 0.684f, 0.762f, 0.834f, 0.91f, 0.953f, 0.976f, 0.991f, 1f
    )
    val clipStops = floatArrayOf(
        0f, 0.2f, 0.25f, 0.3f, 0.35f, 0.4f, 0.45f, 0.5f,
        0.55f, 0.6f, 0.65f, 0.7f, 0.75f, 0.8f, 0.85f, 0.9f, 1f
    )
    for (i in 0 until areaStops.lastIndex) {
        val a0 = areaStops[i]
        val a1 = areaStops[i + 1]
        if (targetAreaFraction <= a1) {
            val t = ((targetAreaFraction - a0) / (a1 - a0)).coerceIn(0f, 1f)
            return clipStops[i] + (clipStops[i + 1] - clipStops[i]) * t
        }
    }
    return 1f
}

private fun createStarPath(width: Float, height: Float): Path {
    val path = Path()
    val cx = width / 2f
    val cy = height / 2f
    val outerRadius = min(width, height) / 2f
    val innerRadius = outerRadius * 0.5f
    val startAngle = -PI / 2.0

    repeat(10) { pointIndex ->
        val angle = startAngle + (PI / 5.0) * pointIndex
        val radius = if (pointIndex % 2 == 0) outerRadius else innerRadius
        val x = cx + cos(angle).toFloat() * radius
        val y = cy + sin(angle).toFloat() * radius
        if (pointIndex == 0) {
            path.moveTo(x, y)
        } else {
            path.lineTo(x, y)
        }
    }
    path.close()
    return path
}

// Figma: Order Status Chips — cornerRadius 200 (pill), padding 8×2
@Composable
fun ReservationStatusBadge(
    status: ReservationStatus,
    isHistoryCard: Boolean = false,
    isPickupExpired: Boolean = false
) {
    val colors = AppTheme.current.colors
    val statusReservedText = colors.onSecondaryContainer
    val statusReservedBg = colors.secondaryContainer
    val statusPaidText = Color(0xFF1565C0)
    val statusPaidBg = Color(0xFFE3F2FD)
    val statusReceivedBg = colors.primaryContainer
    val isCanceledLikeInHistory = isHistoryCard &&
        (status == ReservationStatus.RESERVED || status == ReservationStatus.CANCELED)
    val effectiveStatus = if (isCanceledLikeInHistory) {
        ReservationStatus.CANCELED
    } else {
        status
    }

    var (text, textColor, backgroundColor) = when (effectiveStatus) {
        ReservationStatus.RESERVED -> Triple(sharedString(SR.strings.reserved), statusReservedText, statusReservedBg)
        ReservationStatus.PAID -> Triple(sharedString(SR.strings.paid), statusPaidText, statusPaidBg)
        ReservationStatus.DELIVERED_TO_CLIENT -> Triple(sharedString(SR.strings.received), colors.onPrimaryContainer, statusReceivedBg)
        ReservationStatus.PAID_TO_MERCHANT -> Triple(sharedString(SR.strings.received), colors.onPrimaryContainer, statusReceivedBg)
        ReservationStatus.CANCELED -> Triple(sharedString(SR.strings.cancel), Color.White, statusReservedText)
    }
    var borderColor: Color? = null

    if (isCanceledLikeInHistory) {
        text = "Отказана"
    }

    if (isPickupExpired && status == ReservationStatus.PAID) {
        text = sharedString(SR.strings.not_accepted)
        textColor = colors.error
        backgroundColor = Color.White
        borderColor = colors.error
    }

    Card(
        modifier = if (borderColor != null) {
            Modifier.border(1.dp, borderColor, RoundedCornerShape(200.dp))
        } else {
            Modifier
        },
        shape = RoundedCornerShape(200.dp),
        colors = CardDefaults.cardColors(containerColor = backgroundColor)
    ) {
        Texts.LabelSmall(
            modifier = Modifier.padding(horizontal = p8, vertical = p2),
            text = text,
            color = textColor
        )
    }
}

@Composable
private fun SlideToConfirmButton(
    modifier: Modifier = Modifier,
    text: String,
    onSlideComplete: () -> Unit
) {
    val thumbSize = p56
    val triggerThreshold = 0.8f
    val density = LocalDensity.current
    var thumbOffsetPx by remember { mutableStateOf(0f) }
    var swipeState by remember { mutableStateOf(SwipeButtonState.IDLE) }

    BoxWithConstraints(
        modifier = modifier
            .height(p56)
            .clip(RoundedCornerShape(200.dp))
            .background(AppTheme.current.colors.primaryContainer)
    ) {
        val totalWidthPx = constraints.maxWidth.toFloat()
        val thumbSizePx = with(density) { thumbSize.toPx() }
        val maxOffsetPx = (totalWidthPx - thumbSizePx).coerceAtLeast(0f)

        if (thumbOffsetPx > maxOffsetPx) {
            thumbOffsetPx = maxOffsetPx
        }

        val animatedOffset by animateFloatAsState(
            targetValue = thumbOffsetPx,
            animationSpec = if (swipeState == SwipeButtonState.IDLE && thumbOffsetPx == 0f) {
                androidx.compose.animation.core.spring(
                    dampingRatio = androidx.compose.animation.core.Spring.DampingRatioMediumBouncy,
                    stiffness = androidx.compose.animation.core.Spring.StiffnessMedium
                )
            } else {
                tween(durationMillis = 160)
            },
            label = "slideConfirmOffset"
        )

        val draggableState = rememberDraggableState { delta ->
            if (swipeState == SwipeButtonState.IDLE || swipeState == SwipeButtonState.DRAGGING) {
                swipeState = SwipeButtonState.DRAGGING
                thumbOffsetPx = (thumbOffsetPx + delta).coerceIn(0f, maxOffsetPx)
            }
        }

        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            if (swipeState == SwipeButtonState.COMPLETED) {
                Texts.TitleMedium(
                    text = "✓",
                    color = AppTheme.current.colors.onPrimaryContainer,
                    textAlign = TextAlign.Center,
                    textDecoration = null
                )
            } else {
                // Текстът не влиза под thumb-а; шрифтът се смалява, докато се събере на един ред.
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(start = thumbSize + p8, end = p8),
                    contentAlignment = Alignment.Center
                ) {
                    AutoResizeSingleLineTitleMedium(
                        text = text,
                        modifier = Modifier.fillMaxWidth(),
                        color = AppTheme.current.colors.onPrimaryContainer,
                        textAlign = TextAlign.Center
                    )
                }
            }
        }

        Box(
            modifier = Modifier
                .offset {
                    IntOffset(
                        x = animatedOffset.roundToInt(),
                        y = 0
                    )
                }
                .size(thumbSize)
                .clip(RoundedCornerShape(200.dp))
                .background(AppTheme.current.colors.primary)
                .draggable(
                    state = draggableState,
                    orientation = Orientation.Horizontal,
                    enabled = swipeState == SwipeButtonState.IDLE || swipeState == SwipeButtonState.DRAGGING,
                    onDragStopped = {
                        val dragProgress = if (maxOffsetPx > 0f) {
                            (thumbOffsetPx / maxOffsetPx).coerceIn(0f, 1f)
                        } else {
                            0f
                        }
                        if (dragProgress >= triggerThreshold && maxOffsetPx > 0f) {
                            thumbOffsetPx = maxOffsetPx
                            onSlideComplete()
                            swipeState = SwipeButtonState.COMPLETED
                        } else {
                            thumbOffsetPx = 0f
                            swipeState = SwipeButtonState.IDLE
                        }
                    }
                ),
            contentAlignment = Alignment.Center
        ) {
            if (swipeState == SwipeButtonState.COMPLETED) {
                Texts.TitleMedium(
                    text = "✓",
                    color = AppTheme.current.colors.onPrimary
                )
            } else {
                DoubleChevronIcon(
                    modifier = Modifier,
                    color = AppTheme.current.colors.onPrimary
                )
            }
        }
    }
}

@Composable
private fun DoubleChevronIcon(
    modifier: Modifier = Modifier,
    color: Color
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        ChevronIcon(
            modifier = Modifier.size(width = p10, height = p18),
            color = color.copy(alpha = 0.56f)
        )
        ChevronIcon(
            modifier = Modifier.size(width = p10, height = p18),
            color = color
        )
    }
}

@Composable
private fun ChevronIcon(
    modifier: Modifier = Modifier,
    color: Color
) {
    androidx.compose.foundation.Canvas(modifier = modifier) {
        val strokeWidth = 2.8.dp.toPx()
        val start = Offset(x = size.width * 0.2f, y = size.height * 0.14f)
        val middle = Offset(x = size.width * 0.8f, y = size.height * 0.5f)
        val end = Offset(x = size.width * 0.2f, y = size.height * 0.86f)

        drawLine(
            color = color,
            start = start,
            end = middle,
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )
        drawLine(
            color = color,
            start = middle,
            end = end,
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )
    }
}



